import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { ButtonComponent } from '@ppl/ui';
import { BaseComponent } from '../../../../shared/utils/base.component';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { TimesheetService } from '../../../../services/timesheet.service';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DateWithDayOfWeekPipe } from '../../../../services/dateWithDayOfWeek';
import { ListService } from '../../../../shared/services/list.service';
import { TimesheetStaffDeniedReasonsComponent } from '../timesheet-staff-deniedreasons/timesheet-staff-deniedreasons.component';
import { TimesheetStaffErrorcodeComponent } from '../timesheet-staff-errorcode/timesheet-staff-errorcode.component';
import { ErrorAlertService } from '../../../../shared/services/error-alert.service';
import { DatePickerModule } from 'primeng/datepicker';
import { UserService } from '../../../../services/user.service';
import { environment } from '../../../../../environments/environment';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { forkJoin, map, Observable } from 'rxjs';
import CommonUtil from '../../../../shared/authorization/common.util';

@Component({
  selector: 'ppl-timesheets-add-entries',
  standalone: true,
  imports: [
    CommonModule,
    NgZorroModule,
    ButtonComponent,
    ReactiveFormsModule,
    NzDatePickerModule,
    FormsModule,
    TableModule,
    NzToolTipModule,
    DateWithDayOfWeekPipe,
    TimesheetStaffDeniedReasonsComponent,
    TimesheetStaffErrorcodeComponent,
    DatePickerModule
  ],
  templateUrl: './timesheets-add-entries.component.html',
  styleUrl: './timesheets-add-entries.component.css',
})
export class TimesheetNewEntriesComponent
  extends BaseComponent
  implements OnInit {
  private readonly activeAssociationStatuses = ['Association Active', 'Registration Active', 'Association Active with Gap', 'Paperwork Complete'];
  private readonly noAuthorizationErrorMessage = 'No Authorization for pay period and participant.';
  private readonly paperworkIncompleteErrorMessage = 'Participant/employer/provider are not under paperwork complete status.';

  pageId = '';
  addTimesheetForm!: FormGroup;
  showEntries = false;
  reasonList: LookupValueItem[] = [];
  programName!: string;
  @Input() entryType = 'timeEntry';
  @Input() payPeriod: any;
  @Input() consumers: any;
  @Input() action = 'add';
  @Input() timesheetData: any;
  @Input() entryPage: any;
  @Input() staffView = false;
  @Input() isReadOnly = false;
  @Output() timesheetAdded = new EventEmitter();
  @Output() onClose = new EventEmitter();
  serviceCodes: { [index: string]: string[] } = {};
  timesheetDates: string[] = [];
  statusList: LookupValueItem[] = [];
  processingStatusList: LookupValueItem[] = [];
  serviceCodesTO: any;
  enableApplicationStatus = ['S', 'PE'];
  // Penalty service codes that are saved on a timesheet but are NOT returned by
  // getServiceCodesByConsumer. In edit mode we fall back to the data already
  // present on the timesheet service line so the code/description still display.
  nonAuthServiceCodes = ['CA_MEAL_PENALTY', 'REST_PENALTY'];
  serviceChanged: boolean[] = [];
  showFormFields = true;
  error: any;
  errors: any[] = [];
  fieldErrors: Record<number, Record<string, boolean>> = {};
  providerId: any;
  providerPPLId: any;
  isTwoConsumerSC = false;
  pplIdError = false;
  @ViewChild('confirmationDialog') confirmationDialog!: TemplateRef<any>;
  @ViewChild('billedUnitsValidationDialog') billedUnitsValidationDialog!: TemplateRef<any>;
  @ViewChild('pendedDialog') pendedDialog!: TemplateRef<any>;
  @ViewChild('deniedDialog') deniedDialog!: TemplateRef<any>;
  @ViewChild('mealBreakDialog') mealBreakDialog!: TemplateRef<any>;
  errorcodeData: any;
  deniedData: any;
  isConsentRequired = false;
  isTimesheetDeleted = false;
  timeInTimeOutEqualMessage = 'Service Time In cannot be equal to Service Time Out.';
  timeOutLessThanTimeInMessage = 'Service Time In cannot be greater than Service Time Out.';
  timeOutOverNightShiftMessage = 'Overnight shifts must be entered separately after midnight (12:00 AM).';
  isExternal = false;
  isTimesheetLoaded: boolean = false;
  customErrors: string[] = [];
  isAuthFail = false;
  serviceLocations: any = [];
  serviceDescriptions: { [index: number]: string[] } = {};
  mealBreakEntries: Array<{
    entryIndex: number;
    serviceDate: string;
    serviceDayName: string;
    shiftStart: Date;
    shiftEnd: Date;
    serviceTimeInDisplay: string;
    serviceTimeOutDisplay: string;
    totalMinutes: number;
    // Set from the meal-break check response when the first meal break is
    // already waived on file for this shift. A waiver that already exists is
    // not re-asked, and it also blocks the second meal break from being waived.
    mb1WaivedOnFile: boolean;
    mealBreaks: Array<{
      number: 1 | 2;
      taken: boolean | null;
      timeIn: Date | null;
      timeOut: Date | null;
      waived: boolean | null;
    }>;
    onDutyMeals: Array<{
      number: 1 | 2;
      taken: boolean | null;
    }>;
    restBreakNumbers: number[];
    restBreakTaken: boolean | null;
    // Meal breaks the provider already added as their own timesheet line items
    // (service code contains MEAL) on the same date. Used to warn the user and
    // to validate that dialog-entered meal breaks don't overlap them.
    existingMealLines: Array<{
      label: string;
      timeIn: Date;
      timeOut: Date;
      timeInDisplay: string;
      timeOutDisplay: string;
    }>;
  }> = [];
  // Validation messages shown inline inside the meal-break dialog (instead of a
  // popup) so the provider can see exactly why a meal break is not allowed.
  mealBreakErrors: string[] = [];
  pendingAction = '';
  isHandsetView$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches)
    );

  private dialogSvc = inject(DialogService);

  constructor(
    private fb: FormBuilder,
    private timesheetService: TimesheetService,
    private errorAlertService: ErrorAlertService,
    private listService: ListService,
    private userService: UserService,
    private dateFormatService: DateTimeFormatterService,
    private breakpointObserver: BreakpointObserver
  ) {
    super();
  }

  ngOnInit(): void {
    this.isExternal = environment.isExternal;
    this.addTimesheetForm = this.fb.group({
      consumer: [{ value: null, disabled: this.action === 'edit' }],
      period: [{ value: null, disabled: this.action === 'edit' }],
      timesheetEntries: this.fb.array([]),
      isAgreed: [false]
    });
    if (this.entryPage === 'timesheets') {
      this.addTimesheetForm.addControl('providerPplId', this.fb.control('PRV-CASDP-'));
      this.showFormFields = false;
    }

    if (this.staffView) {
      this.showFormFields = false;
      this.showEntries = true;
    }

    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';

    this.reasonList = this.lookupService.getLookupValue('Manual Entry reason');
    this.serviceLocations = this.lookupService.getLookupValue('service_location');
    const userDetails = this.userService.getUserDetails();
    if (!userDetails) {
      console.error('no user details found');
      return;
    }

    this.statusList = this.lookupService.getLookupValue('Business Status');
    this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');

    if (this.action === 'edit') {
      let startDate = '';
      let endDate = '';
      let currentPayPeriod = this.payPeriod?.find(
        (period: any) =>
          period.payrollId === this.timesheetData.payrollScheduleId
      );
      if (this.timesheetData?.lineItem) {
        startDate = this.timesheetData?.lineItem?.serviceStartDate;
        endDate = this.timesheetData?.lineItem?.serviceEndDate;
      } else {
        const period = currentPayPeriod?.payPeriod?.split('-');
        if (period?.length === 2) {
          startDate = period[0];
          endDate = period[1];
        }
      }
      const payload = {
        programCode: this.programName,
        consumerId: this.timesheetData.consumer.consumerId,
        periodFromDate: startDate,
        periodToDate: endDate,
        source: 'timesheet',
        providerId: this.getProviderId()
      };
      this.timesheetService
        .getServiceCodesByConsumer(payload)
        .subscribe((data: any) => {
          if (data.responsedata == "Auth Fail" && data.status == 404) {
            this.isAuthFail = true;
            const date = this.payPeriod.find(
              (period: any) => period.payrollId === this.timesheetData.payrollScheduleId
            );

            if (!date) {
              return;
            }

            this.timesheetEntries.clear();
            const [startDate, endDate] = date?.payPeriod.split('-') || [];
            const startDateObj = new Date(startDate);
            const endDateObj = new Date(endDate);

            this.addTimesheetForm.patchValue({
              consumer: this.timesheetData.consumer.consumerId,
              period: this.timesheetData.payrollScheduleId,
              isAgreed: false
            });

            // A missing authorization shouldn't hide records that are already in
            // a disabled processing status - they are read-only history and can
            // still be displayed from the saved service lines.
            if (this.renderDisabledServiceLines()) {
              return;
            }

            this.handleAuthFailErrors(data, startDateObj, endDateObj);
            return;
          }

          this.isAuthFail = false;
          this.customErrors = [];
          this.processServiceCodes(data.responsedata);
          this.addTimesheetForm.patchValue({
            consumer: this.timesheetData.consumer.consumerId,
            period: this.timesheetData.payrollScheduleId,
            isAgreed: false
          });

          if (
            this.serviceCodes === null ||
            this.serviceCodes === undefined ||
            Object.keys(this.serviceCodes).length < 1
          ) {
            this.showEntries = false;
          } else {
            this.processTimesheetByServiceLinesForStaff();
            this.showEntries = true;
          }
        });
    } else if (this.payPeriod && this.payPeriod.length > 0) {
      this.patchCurrentPayPeriod();
    }
  }

  private processServiceCodes(serviceCodesData: any[]) {
    if (Array.isArray(serviceCodesData)) {
      serviceCodesData.forEach((serviceCode: any) => {
        if (Array.isArray(serviceCode.authList)) {
          serviceCode.authList.forEach((auth: any) => {
            const fromDate = this.dateFormatService.createDateInUTC(auth.fromDate);
            const toDate = this.dateFormatService.createDateInUTC(auth.toDate);
            for (
              let d = fromDate;
              d <= toDate;
              d.setDate(d.getDate() + 1)
            ) {
              const dateStr = this.dateFormatService.formatDateToString(d);
              if (!this.serviceCodes[dateStr]) {
                this.serviceCodes[dateStr] = [];
              }
              // Avoid duplicates
              if (!this.serviceCodes[dateStr].some((sc: any) => sc.serviceCodeDetailId === serviceCode.serviceCodeDetailId)) {
                this.serviceCodes[dateStr].push(serviceCode);
              }
            }
          });
        }
      });
    }
  }

  getConsumersAndPayPeriod() {
    const pplId = this.addTimesheetForm.get('providerPplId')?.value.trim();
    // if user didn't enter anything then show error
    if (pplId === '' || pplId === 'PRV-CASDP-') {
      this.pplIdError = true;
      return;
    }

    this.addTimesheetForm.get('consumer')?.setValue(null);
    console.log('providerpplid', pplId);
    this.providerPPLId = pplId;
    this.pplIdError = pplId === '';
    if (pplId !== '') {
      this.showFormFields = true;
      this.getConsumers(pplId);
      this.getPayPeriod();
    }
  }

  getConsumers(pplId: any) {
    const payload = {
      programCode: this.programName,
      providerId: null,
      providerPplId: pplId,
      consumerId: null,
      consumerPplId: null,
    };
    this.timesheetService
      .getConsumerListByPPLId(payload)
      .subscribe((data: any) => {
        if (data.responsedata) {
          const providerList = data.responsedata.providers;

          if (providerList?.length > 0) {
            const firstId = providerList[0].providerId;
            const allSame = providerList.every(
              (item: any) => item.providerId === firstId
            );

            if (allSame) {
              this.providerId = firstId;
            }
          }
          this.consumers = data.responsedata.consumers ?? [];
        }
      });
  }

  getPayPeriod() {
    this.timesheetService
      .getPayPeriod(this.programName)
      .subscribe((data: any) => {
        this.payPeriod = data.responsedata;

        if (this.action !== 'edit') {
          this.patchCurrentPayPeriod();
          this.enableTimesheetForm();
        }
      });
  }

  patchCurrentPayPeriod() {
    // set current pay period for new timesheet
    const currentDate = new Date();
    const currentPayPeriod = this.payPeriod.find(
      (period: any) => {
        const [startDateStr, endDateStr] = period.payPeriod.split('-');
        const startDate = new Date(startDateStr);
        const endDate = new Date(endDateStr);
        return currentDate >= startDate && currentDate <= endDate;
      });

    this.addTimesheetForm.patchValue({ period: currentPayPeriod?.payrollId });
  }

  convertTimeStringToDate(timeString: string): Date {
    const currentDate = new Date();
    const [hours, minutes] = timeString.split(':');
    currentDate.setHours(Number(hours), Number(minutes), 0, 0); // Set the hours and minutes
    return currentDate;
  }

  get timesheetEntries() {
    return this.addTimesheetForm.get('timesheetEntries') as FormArray;
  }

  generateFormattedDatesFromRange(date: any): string[] {
    const [startDateStr, endDateStr] = date?.payPeriod.split('-') || [];
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    const numDates =
      Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24)) + 1;
    const totalDuration = end.getTime() - start.getTime();
    const stepSize = totalDuration / (numDates - 1);
    const dates: string[] = [];
    const formatDate = (date: Date): string => {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    };
    if (numDates === 1) {
      return [formatDate(start)];
    }
    for (let i = 0; i < numDates; i++) {
      const newDate = new Date(start.getTime() + stepSize * i);
      dates.push(formatDate(newDate));
    }
    return dates;
  }

  isPredefinedRow(index: number): boolean {
    const entry = this.timesheetEntries.at(index);
    const serviceDate = entry.get('serviceDate')?.value;
    return this.timesheetDates.includes(serviceDate);
  }

  addTimesheetEntry(date: any): void {
    const today = new Date();
    const timeOffThreaholdDate = new Date();
    timeOffThreaholdDate.setDate(today.getDate() - 15);
    today.setHours(0, 0, 0, 0);
    const entryDate = new Date(date);
    entryDate.setHours(0, 0, 0, 0);
    const isFutureDate = entryDate > today && (this.entryType === 'timeEntry' || this.entryType === '');
    const isBackDate = entryDate < timeOffThreaholdDate && this.entryType === 'timeOff';
    const timesheetEntry = this.fb.group(
      {
        selected: [false],
        serviceDate: [date],
        timeSheetDetailId: [null],
        businessStatusLkp: [{ value: null, disabled: true }],
        applicationStatusLkp: [null],
        applicationStatusLkpCode: [null],
        service: [{ value: null, disabled: isFutureDate || isBackDate }],
        serviceDescription: [null],
        clockInLocation: [null],
        clockOutLocation: [null],
        serviceTimeIn: [{ value: null, disabled: isFutureDate || isBackDate }],
        serviceTimeOut: [{ value: null, disabled: isFutureDate || isBackDate }],
        totalTime: [{ value: null, disabled: true }],
        billedUnits: [{ value: null, disabled: true }],
        manualReasonLkp: [
          { value: null, disabled: isFutureDate || isBackDate },
        ],
        overrideService: [false],
        isOvertimeEntry: [false],
        // Records in a disabled processing status are rendered read-only: the
        // service code/description are shown as plain text from the saved line
        // instead of the editable dropdowns.
        displayOnly: [false],
        serviceCodeDisplay: [null],
        serviceDescriptionDisplay: [null],
        totalTimeOnDisplay: [null]
      },
      { disabled: isFutureDate || isBackDate }
    );
    this.timesheetEntries.push(timesheetEntry);
    if (!isFutureDate && !isBackDate) {
      timesheetEntry.get('serviceTimeIn')?.valueChanges.subscribe(() => {
        this.updateTimesheetUnits(timesheetEntry);
      });

      timesheetEntry.get('serviceTimeOut')?.valueChanges.subscribe(() => {
        this.updateTimesheetUnits(timesheetEntry);
      });
    }
  }

  updateTimesheetUnits(entry: FormGroup): void {
    const serviceTimeIn = entry.get('serviceTimeIn')?.value;
    const serviceTimeOut = entry.get('serviceTimeOut')?.value;
    const serviceCode = entry.get('service')?.value;

    if (!serviceCode || !serviceTimeIn || !serviceTimeOut) {
      entry.get('billedUnits')?.setValue(null);
      return;
    }
    if (serviceTimeOut <= serviceTimeIn) {
      entry.get('serviceTimeOut')?.setErrors({ invalidTimeRange: true });
      return;
    }

    const totalTimeValue = this.dateFormatService.getDuration(serviceTimeIn, serviceTimeOut);
    entry.get('totalTime')?.setValue(totalTimeValue);
    entry.get('billedUnits')?.setValue(1);
  }

  isAddDisabled(entry: any): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const entryDate = new Date(entry.get('serviceDate')?.value);
    entryDate.setHours(0, 0, 0, 0);
    const isFutureDate = entryDate > today && (this.entryType === 'timeEntry' || this.entryType === '');
    const isBackDate = entryDate < today && this.entryType === 'timeOff';
    return this.isAuthFail || isFutureDate || isBackDate;
  }

  addTimeSlot(dateIndex: number): void {
    const entry = this.timesheetEntries.at(dateIndex) as FormGroup;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const entryDate = new Date(entry.get('serviceDate')?.value);
    entryDate.setHours(0, 0, 0, 0);
    const isFutureDate = entryDate > today && this.entryType === 'timeEntry';
    const isBackDate = entryDate < today && this.entryType === 'timeOff';
    const timesheetEntry = this.fb.group({
      selected: [true],
      serviceDate: [entry.get('serviceDate')?.value],
      service: [{ value: null, disabled: isFutureDate || isBackDate }],
      serviceTimeIn: [{ value: null, disabled: isFutureDate || isBackDate }],
      serviceTimeOut: [{ value: null, disabled: isFutureDate || isBackDate }],
      serviceDescription: [null],
      clockInLocation: [null],
      clockOutLocation: [null],
      totalTime: [{ value: null, disabled: true }],
      billedUnits: [{ value: null, disabled: true }],
      manualReasonLkp: [
        { value: null, disabled: isFutureDate || isBackDate },
      ],
      isNewRow: [true]
    });
    this.timesheetEntries.insert(dateIndex + 1, timesheetEntry);
    timesheetEntry.get('serviceTimeIn')?.valueChanges.subscribe(() => {
      this.updateTimesheetUnits(timesheetEntry);
    });

    timesheetEntry.get('serviceTimeOut')?.valueChanges.subscribe(() => {
      this.updateTimesheetUnits(timesheetEntry);
    });
  }


  duplicateRow(index: number): void {
    const entry = this.timesheetEntries.at(index) as FormGroup;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const entryDate = new Date(entry.get('serviceDate')?.value);
    entryDate.setHours(0, 0, 0, 0);
    const isFutureDate = entryDate > today && this.entryType === 'timeEntry';
    const isBackDate = entryDate < today && this.entryType === 'timeOff';
    const isOverrideTimesheet = entry.get('overrideService')?.value;
    const timesheetEntry = this.fb.group({
      selected: [true],
      serviceDate: [entry.get('serviceDate')?.value],
      service: isOverrideTimesheet ? [{ value: null, disabled: isFutureDate || isBackDate }] : [{ value: entry.get('service')?.value, disabled: isFutureDate || isBackDate }],
      serviceTimeIn: [{ value: entry.get('serviceTimeIn')?.value, disabled: isFutureDate || isBackDate }],
      serviceTimeOut: [{ value: entry.get('serviceTimeOut')?.value, disabled: isFutureDate || isBackDate }],
      totalTime: [{ value: entry.get('totalTime')?.value, disabled: true }],
      billedUnits: [{ value: entry.get('billedUnits')?.value, disabled: true }],
      serviceDescription: [entry.get('serviceDescription')?.value],
      clockInLocation: [entry.get('clockInLocation')?.value],
      clockOutLocation: [entry.get('clockOutLocation')?.value],
      manualReasonLkp: [
        { value: entry.get('manualReasonLkp')?.value, disabled: isFutureDate || isBackDate },
      ],
      isNewRow: [true]
    });
    this.timesheetEntries.insert(index + 1, timesheetEntry);
    timesheetEntry.get('serviceTimeIn')?.valueChanges.subscribe(() => {
      this.updateTimesheetUnits(timesheetEntry);
    });

    timesheetEntry.get('serviceTimeOut')?.valueChanges.subscribe(() => {
      this.updateTimesheetUnits(timesheetEntry);
    });

  }

  deleteRow(index: number): void {
    console.log('delete clicked');
    this.timesheetEntries.removeAt(index);
  }

  formatTime(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = '00';

    return `${hours}:${minutes}:${seconds}`;
  }

  transformToServiceLines(input: any, action: string) {
    console.log('input receivwd', input);
    const breakMap = new Map<number, any>();
    this.mealBreakEntries.forEach(entry => breakMap.set(entry.entryIndex, entry));

    let serviceLines: any[] = [];

    input.forEach((entry: any, idx: number) => {
      const breakFields = this.buildBreakFields(breakMap.get(idx));
      serviceLines.push({
        selected: entry.selected,
        serviceDate: entry.serviceDate || '',
        serviceTimeIn: entry.serviceTimeIn ? this.formatTime(new Date(entry.serviceTimeIn)) : null,
        serviceTimeOut: entry.serviceTimeOut ? this.formatTime(new Date(entry.serviceTimeOut)) : null,
        serviceCodeDetailId: entry.service?.serviceCodeDetailId,
        serviceCodeDescription: entry.service?.tasAuthorizationDetailId,
        authIds: entry.serviceDescription,
        clockInLocation: entry.clockInLocation,
        clockOutLocation: entry.clockOutLocation,
        serviceCodeLkp: entry.service?.serviceCodeLkp,
        modifierCodeLkp: entry.service?.modifierCode,
        billedUnits: entry.billedUnits,
        manualReasonLkp: entry.manualReasonLkp,
        timeSheetDetailId: entry.timeSheetDetailId || 0,
        businessStatusLkp:
          this.statusList?.find(
            (status: any) => status.lkpValueName === entry.businessStatusLkp
          )?.lkpValueCode || null,
        applicationStatusLkpCode: entry.applicationStatusLkp,
        applicationStatusLkp:
          this.processingStatusList?.find(
            (status: any) => status.lkpValueName === entry.applicationStatusLkp
          )?.lkpValueCode || null,
        ...breakFields,
      });
    });

    serviceLines = serviceLines.filter((e: any) => e.selected);

    return serviceLines;
  }

  private buildBreakFields(breakData: any): any {
    const mb1 = breakData?.mealBreaks?.find((m: any) => m.number === 1);
    const mb2 = breakData?.mealBreaks?.find((m: any) => m.number === 2);
    const odm1 = breakData?.onDutyMeals?.find((o: any) => o.number === 1);
    const odm2 = breakData?.onDutyMeals?.find((o: any) => o.number === 2);
    const restNums: number[] = breakData?.restBreakNumbers ?? [];
    const restAnswer: boolean | null = breakData?.restBreakTaken ?? null;
    const getRest = (n: number): boolean | null => restNums.includes(n) ? restAnswer : null;
    const mb1WaiverEligible = breakData ? this.shouldShowWaiver(1, breakData) : false;
    const mb2WaiverEligible = breakData ? this.shouldShowWaiver(2, breakData) : false;
    // The waiver question is not asked when it is already waived on file, so
    // echo that known answer back instead of clearing it on save. A break the
    // provider says they actually took is never reported as waived.
    const mb1Waived = mb1?.taken === true
      ? null
      : breakData?.mb1WaivedOnFile === true
        ? true
        : (mb1?.taken === false && mb1WaiverEligible ? (mb1.waived ?? null) : null);

    return {
      mbTaken: mb1?.taken ?? null,
      mbInTime: mb1?.taken === true && mb1.timeIn ? this.formatTime(mb1.timeIn) : null,
      mbOutTime: mb1?.taken === true && mb1.timeOut ? this.formatTime(mb1.timeOut) : null,
      mbWaived: mb1Waived,
      mbWaiverAgreed: mb1Waived,
      mb2Taken: mb2?.taken ?? null,
      mb2InTime: mb2?.taken === true && mb2.timeIn ? this.formatTime(mb2.timeIn) : null,
      mb2OutTime: mb2?.taken === true && mb2.timeOut ? this.formatTime(mb2.timeOut) : null,
      mb2Waived: mb2?.taken === false && mb2WaiverEligible ? (mb2.waived ?? null) : null,
      mb2WaiverAgreed: mb2?.taken === false && mb2WaiverEligible ? (mb2.waived ?? null) : null,
      mbOdm1Taken: odm1?.taken ?? null,
      mbOdm2Taken: odm2?.taken ?? null,
      rbTaken: getRest(1),
      rb2Taken: getRest(2),
      rb3Taken: getRest(3),
      rb4Taken: getRest(4),
      rb5Taken: getRest(5),
      rb6Taken: getRest(6),
    };
  }

  createTimesheetPayload(data: any, action: string) {
    const period = this.payPeriod.find(
      (period: any) => period.payrollId === data.period
    );
    console.log('data in submit', data);
    const payload = {
      programCode: this.programName,
      action: action,
      provider: {
        providerId: this.getProviderId(),
      },
      consumer: this.staffView ? this.timesheetData.consumer : this.consumers.find(
        (consumer: any) => consumer.consumerId === data.consumer
      ),
      payrollScheduleId: period.payrollId,
      serviceLines: this.transformToServiceLines(data.timesheetEntries, action),
      source: this.entryPage === 'timesheets' ? "StaffView" : null,
    };
    return payload;
  }

  checkService(event: any, index: any) {
    this.serviceChanged[index] = true;
    this.updateTimesheetUnitsOnSC(index);
  }

  updateTimesheetUnitsOnSC(index: any) {
    const entry = this.timesheetEntries.at(index) as FormGroup;
    this.updateTimesheetUnits(entry);
  }

  onMoveBack() {
    const formData = this.addTimesheetForm.getRawValue();
    let timeEnties = formData.timesheetEntries
    let selectedTimeEntries = timeEnties.filter((e: any) => e.selected && this.moveBackStatuses.includes(e.applicationStatusLkpCode));

    if (selectedTimeEntries.length == 0) {
      const errorMessage = `Please select the timesheets with a processing status of 'Ready for Payment'.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    const timesheetIds = selectedTimeEntries.map((e: any) => e.timeSheetDetailId);

    const payload = {
      timesheetIds: timesheetIds
    }

    this.timesheetService.moveBackTimesheet(this.programName, payload).subscribe((data: any) => {
      if (data.status === 200) {
        this.timesheetAdded.emit({ timesheetAdded: true });
      }
    });
  }

  evaluateLongShiftHoursAndSave(action: string) {
    let longShiftRecorded = false;
    this.timesheetEntries.controls.forEach((entryGroup: any, index: number) => {
      const isEntrySelected = entryGroup.get('selected')?.value ?? false;

      if (isEntrySelected) {
        const timeIn = entryGroup.get('serviceTimeIn')?.value;
        const timeOut = entryGroup.get('serviceTimeOut')?.value;

        if (timeIn && timeOut) {
          const inTimeInMinutes = (timeIn.getHours() * 60) + (timeIn.getMinutes());
          const outTimeInMinutes = (timeOut.getHours() * 60) + (timeOut.getMinutes());
          const billedTime = outTimeInMinutes - inTimeInMinutes;
          if (billedTime > 780) {
            longShiftRecorded = true;
          }
        }
      }
    });

    if (longShiftRecorded) {
      const dialogConfig: DialogConfig = {
        title: 'Confirmation',
        okText: 'Yes',
        size: 'medium',
        position: 'top',
        showClose: true,
        showButtons: true,
      };

      this.dialogSvc.openDialog(
        this.billedUnitsValidationDialog,
        dialogConfig,
        () => this.checkMealBreaksBeforeSave(action),
        () => this.handleCancel()
      );
    } else {
      this.checkMealBreaksBeforeSave(action);
    }
  }

  checkMealBreaksBeforeSave(action: string) {
    const formData = this.addTimesheetForm.getRawValue();
    const consumerId = this.staffView ? this.timesheetData?.consumer?.consumerId : formData.consumer;
    const providerId = this.getProviderId();

    const selectedIndexes: number[] = [];
    formData.timesheetEntries.forEach((e: any, idx: number) => {
      // PTO (paid time off) and MEAL lines are not worked shifts that require a
      // meal break - PTO is time off, and a MEAL line is itself the meal break -
      // so skip both from the meal-break check entirely.
      const code: string = (e.service?.serviceCodeLkp ?? '').toUpperCase();
      if (e.selected && e.serviceTimeIn && e.serviceTimeOut && !code.includes('PTO') && !code.includes('MEAL')) {
        selectedIndexes.push(idx);
      }
    });

    if (selectedIndexes.length === 0) {
      this.onSave(action);
      return;
    }

    // Group selected shifts by service date. Each date is checked with a single
    // call whose payload is always an array of objects tagged with a 1-based id
    // (even when the date has only one shift), so the response array can be
    // matched back to the right shift via that id. Different dates still fire as
    // separate parallel calls, preserving the previous behaviour.
    const groupsByDate = new Map<string, number[]>();
    selectedIndexes.forEach(idx => {
      const date = formData.timesheetEntries[idx].serviceDate;
      const group = groupsByDate.get(date) ?? [];
      group.push(idx);
      groupsByDate.set(date, group);
    });

    const buildCheckPayload = (idx: number, id: number) => {
      const entry = formData.timesheetEntries[idx];
      return {
        id,
        consumerId,
        providerId,
        serviceDate: entry.serviceDate,
        serviceTimeIn: this.formatTime(new Date(entry.serviceTimeIn)),
        serviceTimeOut: this.formatTime(new Date(entry.serviceTimeOut)),
      };
    };

    // Each request resolves to a list of { entryIdx, promptList, totalMinutes }
    // so every date's response is processed uniformly.
    const requests = Array.from(groupsByDate.values()).map(group => {
      const payload = group.map((idx, i) => buildCheckPayload(idx, i + 1));
      return this.timesheetService.checkMealBreak(this.programName, payload).pipe(
        map((response: any) => {
          const results: any[] = Array.isArray(response?.responsedata) ? response.responsedata : [];
          // id is 1-based and maps back to the shift at group[id - 1].
          return results
            .map((result: any) => ({
              entryIdx: group[result.id - 1],
              promptList: result.promptList ?? [],
              totalMinutes: result.totalMinutes ?? 0,
              mb1Waived: (result.mb1waived ?? result.mb1_waived) === true,
            }))
            .filter(r => r.entryIdx !== undefined);
        })
      );
    });

    // Preserve any answers the user already entered on a previous attempt
    // (e.g. when the save failed) so re-submitting doesn't reset selections.
    const previousEntries = this.mealBreakEntries;

    const mealLinesByDate = this.collectMealBreakLineItems(formData.timesheetEntries);

    forkJoin(requests).subscribe((grouped: Array<Array<{ entryIdx: number; promptList: string[]; totalMinutes: number; mb1Waived: boolean }>>) => {
      const entries: typeof this.mealBreakEntries = [];
      // Sort by entry index so dialog blocks appear in service-date order.
      grouped.flat().sort((a, b) => a.entryIdx - b.entryIdx).forEach(({ entryIdx, promptList, totalMinutes, mb1Waived }) => {
        const builtEntry = this.buildMealBreakEntryFromCheck(entryIdx, promptList, totalMinutes, mb1Waived, formData, mealLinesByDate);
        if (builtEntry) {
          entries.push(builtEntry);
        }
      });
      this.mealBreakEntries = this.restorePreviousMealBreakSelections(entries, previousEntries);
      this.mealBreakErrors = [];

      if (this.mealBreakEntries.length > 0) {
        this.pendingAction = action;
        const dialogConfig: DialogConfig = {
          title: 'Meal & Rest Break Required',
          size: 'large',
          position: 'top',
          showClose: true,
          showButtons: false,
        };
        this.dialogSvc.openDialog(
          this.mealBreakDialog,
          dialogConfig,
          () => { },
          () => this.handleMealBreakCancel()
        );
      } else {
        this.onSave(action);
      }
    });
  }

  // Turns a single meal-break check result into a dialog entry, suppressing
  // prompts already satisfied by an existing MEAL service line. Returns null
  // when there is nothing left to ask the provider for this shift.
  private buildMealBreakEntryFromCheck(
    entryIdx: number,
    promptList: string[],
    totalMinutes: number,
    mb1Waived: boolean,
    formData: any,
    mealLinesByDate: Map<string, typeof this.mealBreakEntries[number]['existingMealLines']>
  ): typeof this.mealBreakEntries[number] | null {
    if (!promptList || promptList.length === 0) {
      return null;
    }

    const entry = formData.timesheetEntries[entryIdx];
    const mealLines = mealLinesByDate.get(entry.serviceDate) ?? [];

    // If the provider already logged a MEAL service line that covers a required
    // meal break (e.g. a 310-MEAL within the first 5 hours of the shift), that
    // break is already compliant. Drop its prompt so we don't ask "Did you take
    // a meal break?" again or generate a duplicate entry.
    const satisfied = this.getSatisfiedMealBreakNumbers(new Date(entry.serviceTimeIn), mealLines);
    let prompts = promptList;
    if (satisfied.size > 0) {
      prompts = prompts.filter(p => {
        if (p === 'MEAL_BREAK_1') return !satisfied.has(1);
        if (p === 'MEAL_BREAK_2') return !satisfied.has(2);
        return true;
      });
    }

    // Every required break for this shift is already covered — nothing to ask.
    // The shift is processed using the existing MEAL service line only.
    if (prompts.length === 0) {
      return null;
    }

    const builtEntry = this.buildMealBreakEntry(entryIdx, entry, prompts, totalMinutes, mb1Waived);
    builtEntry.existingMealLines = mealLines;
    return builtEntry;
  }

  private buildMealBreakEntry(entryIndex: number, entry: any, promptList: string[], totalMinutes: number, mb1Waived = false): typeof this.mealBreakEntries[number] {
    const mealBreaks: any[] = [];
    const onDutyMeals: any[] = [];
    const restBreakNumbers: number[] = [];

    promptList.forEach(prompt => {
      if (prompt === 'MEAL_BREAK_1') {
        mealBreaks.push({ number: 1, taken: null, timeIn: null, timeOut: null, waived: null });
      } else if (prompt === 'MEAL_BREAK_2') {
        mealBreaks.push({ number: 2, taken: null, timeIn: null, timeOut: null, waived: null });
      } else if (prompt === 'ON_DUTY_MEAL_1') {
        onDutyMeals.push({ number: 1, taken: null });
      } else if (prompt === 'ON_DUTY_MEAL_2') {
        onDutyMeals.push({ number: 2, taken: null });
      } else if (prompt.startsWith('REST_BREAK_')) {
        const num = parseInt(prompt.replace('REST_BREAK_', ''), 10);
        if (num >= 1 && num <= 6 && !restBreakNumbers.includes(num)) {
          restBreakNumbers.push(num);
        }
      }
    });

    const shiftStart = new Date(entry.serviceTimeIn);
    const shiftEnd = new Date(entry.serviceTimeOut);

    return {
      entryIndex,
      serviceDate: entry.serviceDate,
      serviceDayName: this.getDayName(entry.serviceDate),
      shiftStart,
      shiftEnd,
      serviceTimeInDisplay: this.formatTimeDisplay(shiftStart),
      serviceTimeOutDisplay: this.formatTimeDisplay(shiftEnd),
      totalMinutes,
      mb1WaivedOnFile: mb1Waived,
      mealBreaks: mealBreaks.sort((a, b) => a.number - b.number),
      onDutyMeals: onDutyMeals.sort((a, b) => a.number - b.number),
      restBreakNumbers: restBreakNumbers.sort((a, b) => a - b),
      restBreakTaken: null,
      existingMealLines: [],
    };
  }

  // Collects meal breaks the provider entered as their own timesheet line items
  // (service code contains MEAL, excluding penalty codes), keyed by service date.
  // These are matched to the dialog entries so we can warn about and validate
  // against them. Only selected rows with both times are considered, since only
  // those will actually be saved.
  private collectMealBreakLineItems(
    timesheetEntries: any[]
  ): Map<string, typeof this.mealBreakEntries[number]['existingMealLines']> {
    const linesByDate = new Map<string, typeof this.mealBreakEntries[number]['existingMealLines']>();

    timesheetEntries.forEach((e: any) => {
      const code: string = (e.service?.serviceCodeLkp ?? '').toUpperCase();
      const isMealLine = code.includes('MEAL') && !this.nonAuthServiceCodes.includes(code);
      if (!e.selected || !isMealLine || !e.serviceTimeIn || !e.serviceTimeOut) {
        return;
      }

      const timeIn = new Date(e.serviceTimeIn);
      const timeOut = new Date(e.serviceTimeOut);
      const list = linesByDate.get(e.serviceDate) ?? [];
      list.push({
        label: e.service?.serviceCodeDetail || 'meal break service line',
        timeIn,
        timeOut,
        timeInDisplay: this.formatTimeDisplay(timeIn),
        timeOutDisplay: this.formatTimeDisplay(timeOut),
      });
      linesByDate.set(e.serviceDate, list);
    });

    return linesByDate;
  }

  // Determines which CA meal-break requirements are already met by existing MEAL
  // service lines on the shift, so their prompts can be suppressed:
  //   - 1st meal break: covered by a MEAL line taken within the first 5 hours.
  //   - 2nd meal break (long shifts): covered by a separate MEAL line taken
  //     within the first 10 hours.
  // Each MEAL line can only satisfy one requirement, so a single early meal
  // doesn't accidentally clear both breaks on a 10+ hour shift.
  private getSatisfiedMealBreakNumbers(
    shiftStart: Date,
    mealLines: typeof this.mealBreakEntries[number]['existingMealLines']
  ): Set<number> {
    const satisfied = new Set<number>();
    if (!mealLines.length) {
      return satisfied;
    }

    const start = shiftStart.getTime();
    const FIVE_HOURS_MS = 5 * 60 * 60 * 1000;
    const TEN_HOURS_MS = 10 * 60 * 60 * 1000;

    // Earliest meal first, so the first one covers the first required break.
    const sorted = [...mealLines].sort((a, b) => a.timeIn.getTime() - b.timeIn.getTime());
    const used = new Set<number>();

    const within = (windowMs: number): number =>
      sorted.findIndex((line, idx) =>
        !used.has(idx)
        && line.timeIn.getTime() >= start
        && line.timeIn.getTime() <= start + windowMs);

    const idx1 = within(FIVE_HOURS_MS);
    if (idx1 >= 0) {
      satisfied.add(1);
      used.add(idx1);
    }

    const idx2 = within(TEN_HOURS_MS);
    if (idx2 >= 0) {
      satisfied.add(2);
      used.add(idx2);
    }

    return satisfied;
  }

  // Copies the user's previously-entered answers onto freshly rebuilt meal
  // break entries (matched by the same shift) so a re-submit after a failed
  // save keeps their selections instead of starting over.
  private restorePreviousMealBreakSelections(
    newEntries: typeof this.mealBreakEntries,
    previousEntries: typeof this.mealBreakEntries
  ): typeof this.mealBreakEntries {
    if (!previousEntries?.length) {
      return newEntries;
    }

    newEntries.forEach(entry => {
      const prev = previousEntries.find(
        p => p.entryIndex === entry.entryIndex
          && p.serviceDate === entry.serviceDate
          && p.shiftStart.getTime() === entry.shiftStart.getTime()
          && p.shiftEnd.getTime() === entry.shiftEnd.getTime()
      );
      if (!prev) {
        return;
      }

      entry.restBreakTaken = prev.restBreakTaken;

      entry.onDutyMeals.forEach(odm => {
        const prevOdm = prev.onDutyMeals.find(o => o.number === odm.number);
        if (prevOdm) {
          odm.taken = prevOdm.taken;
        }
      });

      entry.mealBreaks.forEach(mb => {
        const prevMb = prev.mealBreaks.find(m => m.number === mb.number);
        if (prevMb) {
          mb.taken = prevMb.taken;
          mb.timeIn = prevMb.timeIn;
          mb.timeOut = prevMb.timeOut;
          mb.waived = prevMb.waived;
        }
      });
    });

    return newEntries;
  }

  shouldShowWaiver(mealBreakNumber: number, entry: typeof this.mealBreakEntries[number]): boolean {
    // The check response already told us the first meal break is waived, so
    // there is nothing to ask: not for meal break 1 (the answer is known) and
    // not for meal break 2 (a waived first break rules out waiving the second).
    if (entry.mb1WaivedOnFile) {
      return false;
    }
    if (mealBreakNumber === 1) {
      return entry.totalMinutes <= 360;
    }
    if (mealBreakNumber === 2) {
      if (entry.totalMinutes > 720) return false;
      const mb1 = entry.mealBreaks.find(m => m.number === 1);
      const mb1Waived = mb1?.taken === false && mb1.waived === true;
      return !mb1Waived;
    }
    return false;
  }

  private syncMb2WaiverEligibility(entry: typeof this.mealBreakEntries[number]) {
    const mb2 = entry.mealBreaks.find(m => m.number === 2);
    if (mb2 && !this.shouldShowWaiver(2, entry)) {
      mb2.waived = null;
    }
  }

  private getDayName(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }

  private formatTimeDisplay(date: Date): string {
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) hours = 12;
    return `${hours}:${minutes} ${ampm}`;
  }

  onMealBreakSelectionChange(mb: any, entry: typeof this.mealBreakEntries[number]) {
    if (mb.taken === true) {
      mb.waived = null;
    } else if (mb.taken === false) {
      mb.timeIn = null;
      mb.timeOut = null;
    }
    if (mb.number === 1) {
      this.syncMb2WaiverEligibility(entry);
    }
  }

  onMealBreakWaivedChange(mb: any, entry: typeof this.mealBreakEntries[number]) {
    if (mb.number === 1) {
      this.syncMb2WaiverEligibility(entry);
    }
  }

  onMealBreakSubmit(confirm: () => void) {
    const errors: string[] = [];

    this.mealBreakEntries.forEach(entry => {
      const shiftLabel = `${entry.serviceDate} (${entry.serviceTimeInDisplay} - ${entry.serviceTimeOutDisplay})`;

      if (entry.restBreakNumbers.length > 0 && entry.restBreakTaken === null) {
        errors.push(`${shiftLabel}: please answer the rest break question.`);
      }

      entry.onDutyMeals.forEach(odm => {
        if (odm.taken === null) {
          const label = entry.onDutyMeals.length > 1
            ? `${odm.number === 1 ? '1st' : '2nd'} on-duty meal break`
            : 'on-duty meal break';
          errors.push(`${shiftLabel}: please answer the ${label} question.`);
        }
      });

      entry.mealBreaks.forEach(mb => {
        const mbLabel = entry.mealBreaks.length > 1 ? `Meal Break ${mb.number}` : 'meal break';

        if (mb.taken === null) {
          errors.push(`${shiftLabel}: please answer the ${mbLabel} question.`);
          return;
        }

        if (mb.taken === true) {
          if (!mb.timeIn || !mb.timeOut) {
            errors.push(`${shiftLabel}: please enter both start and end times for ${mbLabel}.`);
            return;
          }
          if (mb.timeIn.getTime() === mb.timeOut.getTime()) {
            errors.push(`${shiftLabel}: ${mbLabel} start time cannot equal end time.`);
            return;
          }
          if (mb.timeOut.getTime() < mb.timeIn.getTime()) {
            errors.push(`${shiftLabel}: ${mbLabel} end time must be after start time.`);
            return;
          }
          if (mb.timeIn.getTime() < entry.shiftStart.getTime() || mb.timeOut.getTime() > entry.shiftEnd.getTime()) {
            errors.push(`${shiftLabel}: ${mbLabel} must fall within the shift window (${entry.serviceTimeInDisplay} - ${entry.serviceTimeOutDisplay}).`);
            return;
          }
        } else {
          if (this.shouldShowWaiver(mb.number, entry) && mb.waived === null) {
            errors.push(`${shiftLabel}: please answer the waiver question for ${mbLabel}.`);
          }
        }
      });

      // Build the full set of meal-break windows for this date: the ones entered
      // in the dialog plus any the provider already added as MEAL line items.
      // No two of them may overlap, otherwise the same minutes would be counted
      // as a break twice.
      const windows: Array<{ label: string; start: number; end: number }> = [];
      entry.mealBreaks
        .filter(mb => mb.taken === true && mb.timeIn && mb.timeOut && mb.timeOut.getTime() > mb.timeIn.getTime())
        .forEach(mb => {
          windows.push({
            label: entry.mealBreaks.length > 1 ? `Meal Break ${mb.number}` : 'meal break',
            start: mb.timeIn!.getTime(),
            end: mb.timeOut!.getTime(),
          });
        });
      entry.existingMealLines.forEach(line => {
        windows.push({ label: `"${line.label}" service line`, start: line.timeIn.getTime(), end: line.timeOut.getTime() });
      });

      for (let x = 0; x < windows.length; x++) {
        for (let y = x + 1; y < windows.length; y++) {
          if (windows[x].start < windows[y].end && windows[y].start < windows[x].end) {
            errors.push(`${shiftLabel}: ${windows[x].label} overlaps ${windows[y].label}. Meal breaks on the same date cannot overlap — please adjust the times.`);
          }
        }
      }
    });

    this.mealBreakErrors = errors;
    if (errors.length > 0) {
      return;
    }

    confirm();
    this.onSave(this.pendingAction);
  }

  handleMealBreakCancel() {
    this.mealBreakEntries = [];
    this.mealBreakErrors = [];
    this.pendingAction = '';
  }

  onSave(action: string) {
    const payload = this.createTimesheetPayload(
      this.addTimesheetForm.getRawValue(),
      action
    );
    let isValid = true;
    this.fieldErrors = {};
    this.isConsentRequired = false;

    if (!this.addTimesheetForm.get('isAgreed')?.value) {
      isValid = false;
      this.isConsentRequired = true;
      const errorMessage = 'Please select the consent to save the timesheet.';
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (!payload.consumer) {
      isValid = false;
      const errorMessage = 'Please select the participant to save the timesheet.';
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    if (this.entryPage === 'timesheets') {
      const pplId = this.addTimesheetForm.get('providerPplId')?.value.trim();
      if (this.providerPPLId != pplId) {
        isValid = false;
        const errorMessage = `Provider has been changed. Please click the 'Get Participants' button to update the participant details before saving.`;
        this.errorAlertService.openModal('error', 'Error', errorMessage);
        return;
      }
    }

    if (payload.serviceLines.length == 0) {
      isValid = false;
      const errorMessage = 'Please select the timesheets to save.';
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    let selectedTimeEntries = payload.serviceLines.filter((e: any) => e.selected && (e.applicationStatusLkp && e.applicationStatusLkp != 'S' && e.applicationStatusLkp != 'PE'));

    if (selectedTimeEntries.length) {
      const errorMessage = `Please select the timesheets with a processing status of 'Saved' or 'Pended'.`;
      this.errorAlertService.openModal('error', 'Error', errorMessage);
      return;
    }

    this.timesheetEntries.controls.forEach((entryGroup: any, index: number) => {
      const isEntrySelected = entryGroup.get('selected')?.value ?? false;

      if (isEntrySelected) {
        const selected = entryGroup.get('selected')?.value;
        const service = entryGroup.get('service')?.value;
        const serviceDescription = entryGroup.get('serviceDescription')?.value;
        const clockInLocation = entryGroup.get('clockInLocation')?.value;
        const clockOutLocation = entryGroup.get('clockOutLocation')?.value;
        const reason = entryGroup.get('manualReasonLkp')?.value;
        const timeIn = entryGroup.get('serviceTimeIn')?.value;
        const timeOut = entryGroup.get('serviceTimeOut')?.value;
        const billedUnits = entryGroup.get('billedUnits')?.value;
        const businessStatus = entryGroup.get('businessStatusLkp')?.value;

        const fields = [
          { name: 'selected', value: selected },
          { name: 'service', value: service },
          { name: 'serviceDescription', value: serviceDescription },
          { name: 'clockInLocation', value: clockInLocation },
          { name: 'clockOutLocation', value: clockOutLocation },
          { name: 'serviceTimeIn', value: timeIn },
          { name: 'serviceTimeOut', value: timeOut },
          { name: 'billedUnits', value: billedUnits },
        ];

        const businessStatusCode = this.statusList?.find(
          (status: any) => status.lkpValueName === businessStatus
        )?.lkpValueCode;

        if (businessStatusCode !== "GP") {
          fields.push({ name: 'manualReasonLkp', value: reason });
        }

        if (timeIn && timeOut && this.isTimeInAndTimeOutInvalid(index, new Date(timeIn), new Date(timeOut))) {
          isValid = false;
          if (!this.fieldErrors[index]) {
            this.fieldErrors[index] = {};
          }
        }

        const filledFields = fields.filter(
          (field) => field.value != null && field.value !== ''
        );
        if (!this.fieldErrors[index]) {
          this.fieldErrors[index] = {};
        }

        fields.forEach((field) => {
          if (
            (!field.value && filledFields.length > 0)
          ) {
            isValid = false;
            this.fieldErrors[index][field.name] = true;
          } else {
            if (this.fieldErrors[index][field.name]) {
              delete this.fieldErrors[index][field.name];
            }
          }
        });
      }
    });

    if (!isValid) {
      this.error = true;
    } else {
      this.error = false;
      this.timesheetService.saveTimesheet(payload, this.action).subscribe((data: any) => {
        if (data.status === 200) {
          this.mealBreakEntries = [];
          this.pendingAction = '';
          this.timesheetAdded.emit({ timesheetAdded: true });
        }
      });
    }
  }

  isTimeInAndTimeOutInvalid(index: number, timeIn: Date, timeOut: Date) {
    const inHours = timeIn.getHours();
    const inMinutes = timeIn.getMinutes();
    const outHours = timeOut.getHours();
    const outMinutes = timeOut.getMinutes();

    const totalMinutesIn = inHours * 60 + inMinutes;
    const totalMinutesOut = outHours * 60 + outMinutes;

    if (totalMinutesIn === totalMinutesOut) {
      if (!this.fieldErrors[index]) {
        this.fieldErrors[index] = {};
      }
      this.fieldErrors[index]['timeInEqualTimeOut'] = true;

      return true;
    }

    if (totalMinutesOut < totalMinutesIn) {
      if (!this.fieldErrors[index]) {
        this.fieldErrors[index] = {};
      }
      if (outHours < 8 || (outHours === 0 && outMinutes === 0)) {
        this.fieldErrors[index]['timeInAfterTimeOutOverNightShift'] = true;
      } else {
        this.fieldErrors[index]['timeInAfterTimeOut'] = true;
      }

      return true;
    }

    return false;
  }

  isFieldEmpty(fieldName: string, index: number): boolean {
    return this.fieldErrors[index]?.[fieldName] ?? false;
  }

  isBilledUnitsNegative(index: number): boolean {
    return this.fieldErrors[index]?.['billedUnits'] ?? false;
  }

  groupByDate(serviceLines: any[]) {
    return serviceLines?.reduce((acc, entry) => {
      const date = entry.serviceDate;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(entry);
      return acc;
    }, {});
  }

  enableTimesheetForm() {
    if (this.action === 'edit') {
      return;
    }

    this.isAuthFail = false;
    this.customErrors = [];

    const selectedPayId = this.addTimesheetForm.get('period')?.value;
    const date = this.payPeriod.find(
      (period: any) => period.payrollId === selectedPayId
    );

    if (!date) {
      return;
    }

    this.timesheetEntries.clear();
    const [startDate, endDate] = date?.payPeriod.split('-') || [];
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);
    const payload = {
      programCode: this.programName,
      consumerId: this.addTimesheetForm.getRawValue().consumer,
      periodFromDate: startDate,
      periodToDate: endDate,
      source: 'timesheet',
      providerId: this.getProviderId()
    };

    if (this.action !== 'edit' && this.entryType !== 'timeOff') {
      if (this.addTimesheetForm.getRawValue().consumer && date?.payPeriod) {
        this.timesheetService
          .getServiceCodesByConsumer(payload)
          .subscribe((data: any) => {
            if (data.responsedata == "Auth Fail" && data.status == 404) {
              this.isAuthFail = true;
              this.handleAuthFailErrors(data, startDateObj, endDateObj);
              return;
            }

            this.isAuthFail = false;
            this.customErrors = [];
            this.processServiceCodes(data.responsedata);
            console.log('serviceCodes', this.serviceCodes);

            if (
              this.serviceCodes === null ||
              this.serviceCodes === undefined ||
              Object.keys(this.serviceCodes).length < 1
            ) {
              this.showEntries = false;
            } else {
              this.showEntries = true;
              const allDatesInPeriod = this.getAllDatesInRange(startDateObj, endDateObj);
              const validDates = this.getValidDates(allDatesInPeriod, this.serviceCodes);
              console.log('valid dates', validDates);
              this.processTimesheet(validDates)
            }
          });
      }
    } else {
      this.showEntries = true;
    }

    if (this.action !== 'add') {
      this.processTimesheet(date);
    }
  }

  handleAuthFailErrors(data: any, startDateObj: any, endDateObj: any) {
    this.customErrors = this.getAuthFailureMessages(data?.responsemsg);
    if (this.timesheetData?.serviceLines) {
      if (this.serviceCodes) {
        this.showEntries = true;
        const allDatesInPeriod = this.getAllDatesInRange(startDateObj, endDateObj);
        const validDates = this.getValidDates(allDatesInPeriod, this.serviceCodes);
        console.log('valid dates', validDates);
        this.processTimesheet(validDates)
        return;
      }
    }

    this.showEntries = false;
    if (this.customErrors.length === 0) {
      this.customErrors = [this.getAuthorizationFailureFallbackMessage()];
    }
    return;
  }

  getAuthorizationFailureFallbackMessage(): string {
    const selectedConsumer = this.getSelectedConsumer();
    if (this.isPaperworkIncompleteAssociationStatus(selectedConsumer?.associationStatus)) {
      return this.paperworkIncompleteErrorMessage;
    }

    return this.noAuthorizationErrorMessage;
  }

  private getSelectedConsumer(): any {
    const selectedConsumerId = this.addTimesheetForm?.getRawValue()?.consumer;
    return this.consumers?.find((consumer: any) => consumer.consumerId === selectedConsumerId);
  }

  private isPaperworkIncompleteAssociationStatus(associationStatus?: string): boolean {
    return typeof associationStatus === 'string'
      && associationStatus.trim().length > 0
      && !this.activeAssociationStatuses.includes(associationStatus);
  }

  private getAuthFailureMessages(responsemsg: unknown): string[] {
    if (Array.isArray(responsemsg)) {
      return responsemsg
        .map((message: any) => {
          if (typeof message === 'string') {
            return message.trim();
          }

          if (typeof message?.desc === 'string') {
            return message.desc.trim();
          }

          return '';
        })
        .filter((message: string) => message.length > 0);
    }

    if (typeof responsemsg === 'string') {
      return responsemsg
        .split(',')
        .map((message) => message.trim())
        .filter((message) => message.length > 0);
    }

    return [];
  }




  getAllDatesInRange(startDate: Date, endDate: Date): string[] {
    const dates = [];
    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      dates.push(this.formatDate(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  }

  getValidDates(allDatesInPeriod: string[], serviceCodes: { [index: string]: string[] }): string[] {
    return Object.keys(serviceCodes).filter(date => {
      return allDatesInPeriod.includes(date);
    });
  }

  createDateInUTC(dateString: string): Date {
    const [year, month, day] = dateString.split("-");
    const formattedDate = `${month}/${day}/${year}`;

    return new Date(formattedDate);
  }

  formatDate(date: Date): string {
    const day = ("0" + date.getDate()).slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  }

  getFilteredServiceCodesForDate(entry: any): any[] {
    const date = entry.get('serviceDate')?.value;
    const overrideService = entry.get('overrideService')?.value;

    if (overrideService) {
      return [entry.get('service')?.value];
    }

    return this.getServiceCodeForServiceDate(date);
  }

  processTimesheet(date: any) {
    if (this.isAuthFail || (this.entryType === 'timeEntry' && date && date.length > 0)) {
      this.timesheetDates = date;
    } else {
      this.timesheetDates = this.generateFormattedDatesFromRange(date);
    }
    console.log('dates list', this.timesheetDates);
    this.timesheetDates?.forEach((serviceDate: any) => {
      if (this.action !== 'edit') {
        this.addTimesheetEntry(serviceDate);
      } else {
        this.processTimesheetByServiceLines(serviceDate);
      }
    });

    this.timesheetEntries?.controls?.forEach((formGroup, index) => {
      let previousValues = { ...formGroup.value };
      formGroup.valueChanges.subscribe((newValue) => {
        Object.keys(newValue).forEach(controlName => {
          const oldValue = previousValues[controlName];
          const currentValue = newValue[controlName];
          if (oldValue !== currentValue && controlName !== 'selected') {
            formGroup.get('selected')?.setValue(true, { emitEvent: false });
          }
        });
        previousValues = { ...newValue };
      });
    });
  }

  processTimesheetByServiceLines(serviceDate: any) {
    const groupedServiceLines = this.groupByDate(
      this.timesheetData?.serviceLines
    );
    const linesForDate = groupedServiceLines[serviceDate] || [];
    if (linesForDate.length > 0) {
      linesForDate.forEach((line: any) => {
        this.addTimesheetEntry(serviceDate);
        this.patchTSValuesForLine(line);
      });
    } else {
      this.addTimesheetEntry(serviceDate);
    }
  }

  processTimesheetByServiceLinesForStaff() {
    const groupedServiceLines = this.groupByDate(
      this.timesheetData?.serviceLines
    );

    for (const [date, lines] of Object.entries(groupedServiceLines)) {
      const records: any = lines;
      records.forEach((line: any) => {
        this.addTimesheetEntry(date);
        this.patchTSValuesForLine(line);
      });
    }

    this.timesheetEntries.controls.forEach((formGroup, index) => {
      let previousValues = { ...formGroup.value };
      formGroup.valueChanges.subscribe((newValue) => {
        Object.keys(newValue).forEach(controlName => {
          const oldValue = previousValues[controlName];
          const currentValue = newValue[controlName];
          if (oldValue !== currentValue && controlName !== 'selected') {
            formGroup.get('selected')?.setValue(true, { emitEvent: false });
          }
        });
        previousValues = { ...newValue };
      });
    });
  }

  // Processing-status codes whose rows can be moved back (kept in sync with onMoveBack): RP = Ready for Payment.
  private readonly moveBackStatuses = ['RP', 'TP'];

  isMoveBackEligible(entryData: any): boolean {
    const status = entryData?.applicationStatusLkp ?? entryData?.processingStatusLkp;
    return this.moveBackStatuses.includes(status);
  }

  isTimesheetDisabled(entryData: any): boolean {
    // The processing status code lives on `applicationStatusLkp` everywhere else
    // in this component. The staff edit flow happens to also expose it as
    // `processingStatusLkp`, but the provider edit flow (getTimesheet) only sends
    // `applicationStatusLkp`. Keying off `applicationStatusLkp` keeps both flows
    // working so saved provider entries stay selectable instead of being forced
    // read-only.
    const processingStatus = entryData?.applicationStatusLkp ?? entryData?.processingStatusLkp;
    if (!entryData || !entryData.serviceCodeLkp || !processingStatus) {
      return true;
    }

    return entryData.serviceCodeLkp.includes('-OTE') || this.nonAuthServiceCodes.includes(entryData.serviceCodeLkp) || this.enableApplicationStatus.indexOf(processingStatus) === -1;
  }

  // Builds read-only entries for any saved service lines that are in a disabled
  // processing status. Used when the current authorization is missing so the
  // user can still review records that don't depend on an active auth.
  // Returns true when at least one such record was rendered.
  private renderDisabledServiceLines(): boolean {
    const disabledLines = (this.timesheetData?.serviceLines ?? []).filter(
      (line: any) => this.isTimesheetDisabled(line)
    );
    if (disabledLines.length === 0) {
      return false;
    }

    const groupedServiceLines = this.groupByDate(disabledLines);
    for (const [date, lines] of Object.entries(groupedServiceLines)) {
      (lines as any[]).forEach((line: any) => {
        this.addTimesheetEntry(date);
        this.patchTSValuesForLine(line);
      });
    }

    this.customErrors = [];
    this.showEntries = true;
    return true;
  }

  patchTSValuesForLine(entryData: any) {
    const entryGroup = this.timesheetEntries.at(
      this.timesheetEntries.length - 1
    );

    const serviceDetails = this.getServiceCodeForServiceDate(entryData.serviceDate);
    let isNonAuthCode = this.nonAuthServiceCodes.includes(entryData.serviceCodeLkp);

    if (!isNonAuthCode && entryData.serviceCodeLkp.includes('-OTE')) {
      isNonAuthCode = true;
    }

    // Penalty service codes (e.g. CA_MEAL_PENALTY, REST_PENALTY) don't have a real
    // serviceCodeDetailId, so matching on it would let a REST_PENALTY line pick up
    // a CA_MEAL_PENALTY service (null == null). For these codes always rebuild the
    // service from the saved line so the correct code/description are shown.
    let selectedService = isNonAuthCode
      ? undefined
      : serviceDetails.find((service: any) =>
          service.serviceCodeDetailId == entryData.serviceCodeDetailId
        );

    let disableTimesheet = false;
    if (!selectedService) {
      disableTimesheet = true;
      selectedService = {
        fromDate: entryData.serviceDate,
        modifierCode: entryData.modifierCodeLkp,
        serviceCodeDetailId: entryData.tasAuthorizationDetailId,
        serviceCodeLkp: entryData.serviceDescription ?? entryData.serviceCodeDescription,
        serviceModifierDescription: entryData.authDesc,
        toDate: entryData.serviceDate,
      };

      // For penalty codes, rebuild the code/description straight from the saved
      // service line so the Service Code and Service Description dropdowns render
      // the value that actually belongs to this entry.
      if (isNonAuthCode) {
        selectedService.serviceCodeDetail = entryData.serviceDescription ?? entryData.serviceCodeDescription;
        selectedService.authList = [
          {
            ids: entryData.tasAuthorizationDetailId,
            desc: entryData.authDesc,
            fromDate: entryData.serviceDate,
            toDate: entryData.serviceDate,
          },
        ];
      }
    }

    const selectedDate = new Date(entryData.serviceDate);
    if (!disableTimesheet && !selectedService) {
      disableTimesheet = true;
    }

    entryGroup?.patchValue({
      selected: false,
      timeSheetDetailId: entryData.timeSheetDetailId,
      businessStatusLkp: this.getLookupValue(
        this.statusList,
        entryData.businessStatusLkp
      ),
      applicationStatusLkp: this.getLookupValue(
        this.processingStatusList,
        entryData.applicationStatusLkp
      ),
      applicationStatusLkpCode: entryData.applicationStatusLkp,
      service: selectedService,
      serviceTimeIn: entryData.serviceTimeIn ? this.convertTimeStringToDate(entryData.serviceTimeIn) : null,
      serviceTimeOut: entryData.serviceTimeOut ? this.convertTimeStringToDate(entryData.serviceTimeOut) : null,
      serviceDescription: entryData.tasAuthorizationDetailId,
      clockInLocation: entryData.clockInLocation,
      clockOutLocation: entryData.clockOutLocation,
      billedUnits: entryData.billedUnits,
      manualReasonLkp: entryData.manualReasonLkp,
      overrideService: disableTimesheet,
      isOvertimeEntry: CommonUtil.isOvertimeRow(entryData)
    });

    // OTE entries are copied from the parent timesheet, so their time in/out and
    // clock locations carry no real meaning. Disable those fields and render '-'.
    if (CommonUtil.isOvertimeRow(entryData)) {
      ['serviceTimeIn', 'serviceTimeOut', 'clockInLocation', 'clockOutLocation'].forEach(
        (controlName) => entryGroup?.get(controlName)?.disable()
      );
    }
    // Records in a disabled processing status (e.g. adjusted/voided) no longer
    // have an editable authorization, so show the service code and description
    // straight from the saved line and derive the total time from billedUnits
    // (minutes) rather than from the time in/out values.
    if (this.isTimesheetDisabled(entryData)) {
      entryGroup?.patchValue({
        displayOnly: true,
        serviceCodeDisplay: entryData.serviceDescription ?? entryData.serviceCodeDescription,
        serviceDescriptionDisplay: entryData.authDesc,
        totalTime: this.dateFormatService.formatUnitsToHoursMinutes(entryData.billedUnits),
        totalTimeOnDisplay: this.dateFormatService.formatUnitsToHoursMinutes(entryData.billedUnits)
      });
    }

    if (
      !this.enableApplicationStatus.includes(entryData.applicationStatusLkp)
    ) {
      const formGroup = entryGroup as FormGroup;
      Object.keys(formGroup.controls).forEach(controlName => {
        if (controlName != 'selected') {
          const control = formGroup.get(controlName);
          if (control) {
            control.disable();
          }
        }
      });
    }

    if (disableTimesheet || this.isTimesheetDisabled(entryData)) {
      const formGroup = entryGroup as FormGroup;
      // Keep the checkbox enabled for move-back-eligible rows (Ready for Payment / TP) so they
      // can still be selected for move back even though the row itself is read-only.
      const keepSelectable = this.isMoveBackEligible(entryData);
      Object.keys(formGroup.controls).forEach(controlName => {
        if (keepSelectable && controlName === 'selected') {
          return;
        }
        formGroup.get(controlName)?.disable();
      });
    }
  }

  get isSaveDisabled(): boolean {
    const isAnyEntryFilled = this.timesheetEntries.controls.some(
      (entryGroup) => {
        const service = entryGroup.get('service')?.value;
        const reason = entryGroup.get('manualReasonLkp')?.value;
        const serviceTimeIn = entryGroup.get('serviceTimeIn')?.value;
        const serviceTimeOut = entryGroup.get('serviceTimeOut')?.value;
        const billedUnits = entryGroup.get('billedUnits')?.value;
        const selected = entryGroup.get('selected')?.value;
        return (
          (service || reason || serviceTimeIn || serviceTimeOut || billedUnits) && selected
        );
      }
    );
    return this.isAuthFail || !isAnyEntryFilled;
  }

  openConfirmationPopup(index: number) {
    const dialogConfig: DialogConfig = {
      title: 'Confirmation',
      okText: 'Yes',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: true,
    };

    this.dialogSvc.openDialog(
      this.confirmationDialog,
      dialogConfig,
      () => this.deleteTimesheet(index),
      () => this.handleCancel()
    );
  }

  handleCancel() {

  }

  deleteTimesheet(index: number): void {
    const entry = this.timesheetEntries.at(index) as FormGroup;
    const serviceDate = entry.get('serviceDate')?.value;
    const id = entry.value.timeSheetDetailId;
    const otherEntries = this.timesheetEntries.controls.filter((entry, i) =>
      i !== index && entry.get('serviceDate')?.value === serviceDate
    );

    const deleteTimesheetsPayload = { programCode: this.programName, timesheetLineDetailIds: [id] };

    this.timesheetService.deleteTimesheet(deleteTimesheetsPayload)
      .subscribe((response: any) => {
        if (response.status === 200) {
          if (otherEntries.length > 0) {
            this.timesheetEntries.removeAt(index);
          } else {
            this.timesheetEntries.removeAt(index);
            this.addEmptyRowAtIndex(serviceDate, index);
          }
          this.isTimesheetDeleted = true;
        } else {
          console.error('Error deleting timesheet:', response);
        }
      });
  }

  addEmptyRowAtIndex(serviceDate: string, index: number): void {
    const newEntry = this.fb.group({
      serviceDate: [serviceDate],
      service: [null],
      serviceDescription: [null],
      clockInLocation: [null],
      clockOutLocation: [null],
      serviceTimeIn: [null],
      serviceTimeOut: [null],
      totalTime: [{ value: null, disabled: true }],
      billedUnits: [{ value: null, disabled: true }],
      manualReasonLkp: [null],
      businessStatusLkp: [null],
      applicationStatusLkp: [null],
      applicationStatusLkpCode: [null],
      timeSheetDetailId: [null],
      selected: [false]
    });
    this.timesheetEntries.insert(index, newEntry);

    newEntry.get('serviceTimeIn')?.valueChanges.subscribe(() => {
      this.updateTimesheetUnits(newEntry);
    });

    newEntry.get('serviceTimeOut')?.valueChanges.subscribe(() => {
      this.updateTimesheetUnits(newEntry);
    });

    this.timesheetEntries.controls.forEach((formGroup, index) => {
      const formGroupValueChanges = formGroup?.valueChanges as EventEmitter<any>;
      if (!formGroupValueChanges?.observed) {
        let previousValues = { ...formGroup.value };
        formGroup.valueChanges.subscribe((newValue) => {
          Object.keys(newValue).forEach(controlName => {
            const oldValue = previousValues[controlName];
            const currentValue = newValue[controlName];
            if (oldValue !== currentValue && controlName !== 'selected') {
              formGroup.get('selected')?.setValue(true, { emitEvent: false });
            }
          });
          previousValues = { ...newValue };
        });
      }
    });
  }

  openPendedDialog() {
    const dialogConfig: DialogConfig = {
      title: `Pended Timesheet`,
      okText: 'Ok',
      size: 'x-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.pendedDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  openDeniedDialog() {
    const dialogConfig: DialogConfig = {
      title: `Denied Timesheet`,
      okText: 'Ok',
      size: 'x-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.deniedDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  handlePendedTimesheet(timeSheetDetailId: any) {
    console.log("timesheetLineId", timeSheetDetailId);
    const filters: any = [
      {
        jsonPath: "timesheetId",
        value: timeSheetDetailId,
        operator: "Equal"
      }
    ];
    const errorListPayload = this.buildRequestPayload(
      'timesheet-processCode',
      filters
    );
    this.listService
      .getListData(errorListPayload, 'claims')
      .subscribe((data: any) => {
        this.errorcodeData = data.responsedata;
        this.openPendedDialog();
      });
  }

  rowClickedTs(status: any, timeSheetDetailId: any) {
    if (status === 'Pended') {
      this.handlePendedTimesheet(timeSheetDetailId);
    }
    else if (status === 'Denied') {
      const payload = {
        pageName: 'timesheet-processCode',
        programCode: this.programName,
        condition: 'and',
        recordsPerPage: 20,
        startingPageNumber: 1,
        searchParam: [
          {
            jsonPath: 'timesheetId',
            value: timeSheetDetailId,
            operator: 'Equal',
          },
        ],
        orderByColumn: ['id'],
        orderBy: ['desc'],
      };
      this.listService
        .getListData(payload, 'claims')
        .subscribe((data: any) => {
          this.deniedData = data.responsedata;
          this.openDeniedDialog();
        });
    }
  }

  buildRequestPayload(pageName: string, searchParams: []) {
    if (searchParams.length == 0) {
      //handle the error here
      return;
    }

    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: 20,
      startingPageNumber: '1',
      searchParam: searchParams,
      orderByColumn: [],
      orderBy: ['desc'],
    };
    return payload;
  }

  onCancel() {
    this.onClose.emit({ isTimesheetDeleted: this.isTimesheetDeleted });
  }

  getProviderId() {
    return this.staffView ? this.timesheetData?.provider?.providerId :
      this.entryPage === 'timesheets'
        ? this.providerId
        : this.userService.getSelectedLead()!;
  }

  getServiceDescriptionsBasedOnServiceDate(serviceDate: string, selectedService: any): any[] {
    if (!selectedService) {
      return [];
    }

    const serviceCodesForDate = this.getServiceCodeForServiceDate(serviceDate);
    if (!serviceCodesForDate || serviceCodesForDate.length === 0) {
      // Penalty service codes are not in the API response, so fall back to the
      // authList we built from the saved service line in patchTSValuesForLine.
      return selectedService.authList ?? [];
    }

    // Find the service for the selectedService
    const matchedService = serviceCodesForDate.find(
      (opt: any) =>
        opt.serviceCodeDetailId === selectedService.serviceCodeDetailId
    );

    if (!matchedService || !matchedService.authList) {
      // Penalty service codes are not in the API response, so fall back to the
      // authList we built from the saved service line in patchTSValuesForLine.
      return selectedService.authList ?? [];
    }

    return matchedService.authList;
  }

  getReasonOptionsForEntry(entry: any): LookupValueItem[] {
    const currentReason = entry?.get('manualReasonLkp')?.value;
    // Penalty entries (e.g. CA_MEAL_PENALTY) come back from the API with a reason
    // such as COMPLIANCE that is not part of the standard 'Manual Entry reason'
    // lookup. Append it as an option so it still shows in the dropdown.
    if (
      currentReason &&
      !this.reasonList.some((opt) => opt.lkpValueCode === currentReason)
    ) {
      return [
        ...this.reasonList,
        { lkpValueCode: currentReason, lkpValueName: currentReason },
      ];
    }

    return this.reasonList;
  }

  getServiceCodeForServiceDate(serviceDate: string): any[] {
    if (!serviceDate) {
      return [];
    }

    const codes = this.serviceCodes[serviceDate] || [];
    return [...codes].sort((a: any, b: any) =>
      (a.serviceCodeDetail || '').localeCompare(b.serviceCodeDetail || '')
    );
  }
}
